import { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  Award,
  Shield,
  Plus,
  Edit,
  Search,
  CheckCircle,
  XCircle,
  BarChart3,
  Zap,
  RefreshCw,
} from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { InputText } from "../../../components/InputText";
import { Label } from "../../../components/ui/label";
import { PatenteModal } from "../../../components/modals/PatenteModal";
import { api } from "../../../services/api";

interface Patente {
  id: string;
  patente: string;
  abreviacao: string;
  ordem: number;
  categoria: string;
  img: string | null;
  prazo: number | null;
  id_habbo_variavel?: number | null;
  status?: "A" | "I";
  quantidade_militares?: number;
  criado_em: string;
  alterado_em: string;
}

export function Patentes() {
  const [patentes, setPatentes] = useState<Patente[]>([]);
  const [filteredPatentes, setFilteredPatentes] = useState<Patente[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPatente, setEditingPatente] = useState<Patente | null>(null);

  useEffect(() => {
    fetchPatentes();
  }, []);

  const fetchPatentes = async () => {
    try {
      setLoading(true);
      const response = await api.get("/patentes");
      setPatentes(response.data);
      setFilteredPatentes(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar patentes", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Filtrar patentes quando o termo de busca mudar
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPatentes(patentes);
    } else {
      const filtered = patentes.filter((patente) =>
        patente.patente.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patente.abreviacao.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPatentes(filtered);
    }
  }, [searchTerm, patentes]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Busca já é feita em tempo real pelo useEffect
  };

  const openCreateModal = () => {
    setEditingPatente(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPatente(null);
  };

  const handleSaveSuccess = () => {
    closeModal();
    fetchPatentes();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Award className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Patentes
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie as patentes militares do sistema
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conteúdo Principal */}
        <div className="lg:col-span-2 space-y-6">

          {/* Filtros de Busca */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                <Search className="w-5 h-5" />
                Buscar Patentes
              </CardTitle>
              <CardDescription>
                Digite o nome da patente ou abreviação para pesquisar ou deixe em branco para ver todas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="search">Nome da Patente</Label>
                  <InputText
                    id="search"
                    name="search"
                    title=""
                    placeholder="Digite o nome da patente ou abreviação..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                  <Button
                    type="button"
                    onClick={openCreateModal}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Patente
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
              <CardDescription>
                {filteredPatentes.length} patente(s) encontrada(s) - ordenadas por hierarquia
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : filteredPatentes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma patente encontrada
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredPatentes
                    .sort((a, b) => b.ordem - a.ordem)
                    .map((patente) => (
                      <div
                        key={patente.id}
                        className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[oklch(0.45_0.15_142)]/10 rounded-lg flex items-center justify-center">
                            <span className="text-sm font-bold text-[oklch(0.45_0.15_142)]">
                              {patente.ordem}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">{patente.patente}</h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span>Abreviação: <span className="font-medium">{patente.abreviacao || "N/A"}</span></span>
                              <span>Categoria: <span className="font-medium">{patente.categoria}</span></span>
                              <span>ID Habbo: <span className="font-medium">{patente.id_habbo_variavel ?? "N/A"}</span></span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={
                                patente.status === "A"
                                  ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700"
                                  : "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600"
                              }>
                                {patente.status === "A" ? (
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                ) : (
                                  <XCircle className="h-3 w-3 mr-1" />
                                )}
                                {patente.status === "A" ? "Ativo" : "Inativo"}
                              </Badge>
                              <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                                <Shield className="h-3 w-3 mr-1" />
                                Ordem {patente.ordem}
                              </Badge>
                            </div>
                            {patente.quantidade_militares && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {patente.quantidade_militares} militares com esta patente
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingPatente(patente);
                              setIsModalOpen(true);
                            }}
                            className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10 dark:text-[oklch(0.75_0.15_142)] dark:border-[oklch(0.45_0.15_142)]/40 dark:hover:bg-[oklch(0.45_0.15_142)]/20"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Painel Lateral */}
        <div className="space-y-6">
          {/* Estatísticas */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Estatísticas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total de Patentes</span>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                    {patentes.length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Patentes Ativas</span>
                  <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                    {patentes.filter(patente => patente.status === "A").length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Patentes Inativas</span>
                  <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600">
                    {patentes.filter(patente => patente.status === "I").length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Maior Hierarquia</span>
                  <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700">
                    {patentes.length > 0 ? Math.max(...patentes.map(p => p.ordem)) : 0}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ações Rápidas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Ações Rápidas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button
                  onClick={openCreateModal}
                  className="w-full justify-start bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Patente
                </Button>
                <Button
                  onClick={() => {
                    setSearchTerm("");
                    fetchPatentes();
                  }}
                  variant="outline"
                  className="w-full justify-start"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar Lista
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <PatenteModal
        isOpen={isModalOpen}
        onClose={closeModal}
        editingPatente={editingPatente}
        onSuccess={handleSaveSuccess}
      />
    </div>
  );
}
